import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import React, { useState } from "react"
import { FormProvider } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import { usePricingPackage } from "../hooks/usePricingPackage"

import { PricingPackageTable } from "./PricingPackageTable"

import { Button } from "@/components/common/Button"
import { formattedTierData } from "@/constants/pricingTiers"
import type {
  CreatePricingPackageFailure,
  PricingPackage,
  Product,
  UpdatePricingPackageFailure,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { AddPricingValues } from "@/screens/Products/components/PricingTiers/AddPricingSchema"
import AddPricingTiersModal from "@/screens/Products/components/PricingTiers/AddPricingTiersModal"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface PricingAndShippingTermsProps {
  product: Product
  sourceId: string
  currentPricing?: PricingPackage
  isEmptyFuturePricing?: boolean
  reexecuteQueryWithReset: () => void
}

export const PricingAndShippingTerms: React.FC<PricingAndShippingTermsProps> = ({
  product,
  sourceId,
  currentPricing,
  isEmptyFuturePricing = false,
  reexecuteQueryWithReset,
}) => {
  const { productId, locationsAssociations, company } = product

  const isAddPricingButtonVisible = !!locationsAssociations?.length

  const [open, setOpen] = useState(false)

  const notify = useNotificationsStore(useShallow(({ enqueueNotification }) => enqueueNotification))
  const { createPricingTier, methods, updatePricingTier, onPricingEditClick } = usePricingPackage()

  const handleSubmit = async (data: AddPricingValues) => {
    const pricingPackageId = methods.getValues("pricingPackageId")

    const { pricingPackage, pricingTiers } = formattedTierData({
      ...data,
      companyId: company?.companyId,
      productId,
      sourceCompanyLocationAssociationId: sourceId,
    })

    const input = pricingPackageId
      ? {
          pricingPackageToUpdate: {
            ...pricingPackage,
            sourceCompanyLocationAssociationId: sourceId,
            companyId: company?.companyId,
            pricingPackageId,
            productId,
          },
          pricingTiersToUpdate: pricingTiers,
        }
      : {
          pricingPackageToCreate: pricingPackage,
          pricingTiersToCreate: pricingTiers,
        }

    const { error, data: result } = await (pricingPackageId
      ? updatePricingTier({ input })
      : createPricingTier({ input }))

    const failure = pricingPackageId
      ? (result?.pricingPackage?.update as UpdatePricingPackageFailure)?.error
      : (result?.pricingPackage?.create as CreatePricingPackageFailure)?.error

    if (error || failure) {
      notify({
        type: NotificationType.Error,
        message: `Pricing and Shipping terms ${pricingPackageId ? "update" : "creation"} failed`,
      })
    } else {
      notify({
        type: NotificationType.Success,
        message: `Pricing and Shipping terms successfully ${pricingPackageId ? "updated" : "added"}`,
      })
      reexecuteQueryWithReset()
    }
    methods.reset()
    setOpen(false)
  }

  const renderContent = () => {
    if (isEmptyFuturePricing && isAddPricingButtonVisible) {
      return (
        <Box className='flex justify-end'>
          <Button variant='primary' appearance='text' startIcon={<AddOutlined />} onClick={() => setOpen(true)}>
            Pricing and Shipping terms
          </Button>
        </Box>
      )
    }

    if (currentPricing) {
      return (
        <PricingPackageTable
          title='Current Pricing'
          pricingDetails={currentPricing}
          onEditClick={(uomName) => {
            onPricingEditClick(currentPricing, uomName)
            setOpen(true)
          }}
        />
      )
    }

    return null
  }

  return (
    <>
      {renderContent()}

      <FormProvider {...methods}>
        <AddPricingTiersModal
          open={open}
          product={product}
          onClose={() => {
            setOpen(false)
          }}
          onSubmit={handleSubmit}
        />
      </FormProvider>
    </>
  )
}
